import { GATEWAY_TYPE, integrationType } from "../Gateways/constants";
import { newIntegrationType } from "../Gateways/constants";
import {
  SERVICES,
  serviceGatewayType,
  availableServiceGatewayType,
  serviceListType,
  servicesType,
  CUSTOM_FORM_SERVICES_LIST,
  SERVICE_OPTION_LABELS,
} from "./constants";
import dayjs from "dayjs";
import { compareArraysWithSort } from "../../system/helpers/helpfulFunctions";
import { conditionType } from "./components/restriction/constants";

const getServiceByAlias = (alias: string) =>
  Object.values(SERVICES).find((item) => item.alias === alias);

export const isServiceSupported = (alias: string): boolean =>
  getServiceByAlias(alias) !== undefined;

export const getServiceName = (alias: string): string =>
  getServiceByAlias(alias).name;

export const getServiceType = (alias: string): string =>
  getServiceByAlias(alias).type;

export const getServiceOptionLabel = (option: string): string =>
  SERVICE_OPTION_LABELS[option] || option;

export const prepareNewService = (alias: string): newIntegrationType => {
  return {
    id: alias,
    active: false,
    alias,
    name: getServiceName(alias),
    settings: {},
    type: getServiceType(alias),
    options: [],
  };
};

export const getServices = (serviceList: serviceListType): servicesType => {
  return serviceList
    ? Object.entries(serviceList)
        .filter(([key]) => isServiceSupported(key))
        .map(([key, value]) =>
          !value
            ? prepareNewService(key)
            : { ...value, name: getServiceName(key) }
        )
    : [];
};

export const getAvailableServiceGateways = (
  gateways: integrationType[],
  serviceGateways: serviceGatewayType[]
): availableServiceGatewayType[] => {
  const availableGateways = gateways.filter(
    (gateway) =>
      !serviceGateways.some(
        (serviceGateway) => serviceGateway.id === gateway.id
      )
  );

  const availableServiceGateways = availableGateways.map((gateway) => {
    if (
      gateway.type === GATEWAY_TYPE.SMS ||
      gateway.type === GATEWAY_TYPE.TELEGRAM
    ) {
      return {
        active: false,
        id: gateway.id,
        name: gateway.name,
        phone_code: [],
      };
    }

    return {
      active: false,
      id: gateway.id,
      name: gateway.name,
    };
  });

  return availableServiceGateways;
};

export const getNewServiceInitialValues = (data: newIntegrationType) => {
  const initialValues = {
    active: data?.active,
    alias: data?.alias,
    name: getServiceName(data?.alias),
    settings: getServiceSettings(data?.alias, data?.settings),
    type: data?.type,
  };

  return initialValues;
};

export const getIntegrationInitialValues = (data: integrationType) => {
  const initialValues = {
    id: data?.id,
    brand_id: data?.brand_id,
    alias: data?.alias,
    name: getServiceName(data?.alias),
    active: data?.active,
    type: data?.type,
    created_at: data?.created_at,
    updated_at: data?.updated_at,
    options: data?.options,
    settings: getServiceSettings(data?.alias, data?.settings),
  };

  return initialValues;
};

export const getServiceSettings = (alias, settings: any = {}) => {
  if (alias === SERVICES.NOTIFICATION_KYC.alias) {
    return {
      ...settings,
      email: {
        ...settings?.email,
        active: settings?.email?.active || true,
      },
    };
  }

  if (alias === SERVICES.BTAG_MAPPING.alias) {
    return settings?.map
      ? {
          map: Object.entries(settings?.map)?.map(([key, value], index) => ({
            passcode: key,
            btag: value,
            id: index,
          })),
        }
      : { map: [{ passcode: "", btag: "", id: 0 }] };
  }

  return { ...settings };
};

export const prepareServiceSettings = (values) => {
  const { alias, settings, type, name } = values;
  if (alias === SERVICES.NOTIFICATION_KYC.alias) {
    return {
      ...settings,
      email: {
        ...settings?.email,
        active: settings?.email?.active || true,
      },
      type,
      name,
      alias,
    };
  }

  if (
    alias === SERVICES.PHONE_VERIFICATION.alias ||
    alias === SERVICES.TWO_FACTOR_AUTHENTICATION.alias ||
    alias === SERVICES.BLACKBOX.alias
  ) {
    return {
      ...settings,
      type,
      name,
      alias,
    };
  }

  if (alias === SERVICES.BTAG_MAPPING.alias) {
    return settings?.map
      ? {
          map: settings?.map.reduce(
            (acc, { btag, passcode }) =>
              passcode ? { ...acc, [passcode]: btag } : acc,
            {}
          ),
        }
      : { map: [] };
  }

  if (alias === SERVICES.LICENCE.alias) {
    Object.keys(settings).forEach((key) => {
      if (settings[key]?.api_key && settings[key]?.domain_types?.length > 0) {
        settings[key].active = settings[key].active || false;
      }
    });
  }

  return settings;
};

export const getTableColumnDateSorter = (field) => (a, b) =>
  dayjs(a[field])?.valueOf() - dayjs(b[field])?.valueOf();

export const formatTimestamp = (timestamp: string, timestampFormat) =>
  dayjs(timestamp).format(timestampFormat);

export const checkIsServiceCustomForm = (alias: string): boolean => {
  return CUSTOM_FORM_SERVICES_LIST.includes(alias);
};

export const checkKycConditionSets = (values: any, currentIndex: number) => {
  if (values?.length === 0 || currentIndex === 0) {
    return true;
  }

  const currentAntifraudArr = values?.[currentIndex]?.antifraud_status;
  const currentKycArr = values?.[currentIndex]?.kyc_status;

  if (!currentAntifraudArr?.length && !currentKycArr?.length) {
    return true;
  }

  for (let i = currentIndex - 1; i >= 0; i--) {
    const equalAntifraud = compareArraysWithSort(
      currentAntifraudArr,
      values?.[i]?.antifraud_status
    );
    const equalKyc = compareArraysWithSort(
      currentKycArr,
      values?.[i]?.kyc_status
    );

    if (equalAntifraud && equalKyc) {
      return false;
    }
  }

  return true;
};

export const checkConditionSets = (
  values: conditionType[],
  currentIndex: number
) => {
  if (values?.length === 0 || currentIndex === 0) {
    return true;
  }

  const currentAntifraudArr =
    values?.[currentIndex]?.conditions?.antifraud_status?.value || [];
  const currentKycArr =
    values?.[currentIndex]?.conditions?.kyc_status?.value || [];
  const currentBtagArr = values?.[currentIndex]?.conditions?.btag?.value || [];
  const currentTagArr = values?.[currentIndex]?.conditions?.tag?.value || [];

  if (
    !currentAntifraudArr?.length &&
    !currentKycArr?.length &&
    !currentBtagArr?.length &&
    !currentTagArr?.length
  ) {
    return true;
  }


  const sortById = (a: string, b: string) => Number(a) - Number(b);

  for (let i = currentIndex - 1; i >= 0; i--) {
    const equalAntifraud = compareArraysWithSort(
      currentAntifraudArr,
      values?.[i]?.conditions?.antifraud_status?.value || []
    );
    const equalKyc = compareArraysWithSort(
      currentKycArr,
      values?.[i]?.conditions?.kyc_status?.value || []
    );

    const equalBtag = compareArraysWithSort(
      currentBtagArr,
      values?.[i]?.conditions?.btag?.value || []
    );

    const otherTagArr = values?.[i]?.conditions?.tag?.value || [];
    const currentTagSorted = [...currentTagArr].sort(sortById);
    const otherTagSorted = [...otherTagArr].sort(sortById);
    const equalTag =
      currentTagArr.length === otherTagArr.length &&
      currentTagSorted.every((id, idx) => id === otherTagSorted[idx]);

    if (equalAntifraud && equalKyc && equalBtag && equalTag) {
      return false;
    }
  }

  return true;
};
